import { Bot } from 'lucide-react';
import type { ChatMessage } from 'server';
import { cn } from '../lib/utils';

type ChatConversationProps = {
  messages: ChatMessage[];
  removeUserOptions?: (content: string | Array<unknown> | null | undefined) => string;
  className?: string;
  agentName?: string;
};

export const ChatConversation = ({
  messages,
  removeUserOptions = (content) => (typeof content === 'string' ? content : ''),
  className,
  agentName
}: ChatConversationProps) => {
  const renderMessageContent = (content: string | undefined) => {
    if (!content) return '';

    // Handle markdown links
    const markdownLinkRegex = /\[([^\]]+)\]\(((?:https?:\/\/|mailto:)[^\s)]+)\)/g;
    const linkPlaceholders: Array<{ text: string; url: string; isEmail: boolean }> = [];
    const processedContent = content.replace(markdownLinkRegex, (_, text, url) => {
      const isEmail = url.startsWith('mailto:');
      linkPlaceholders.push({ text, url, isEmail });
      return `__LINK_${linkPlaceholders.length - 1}__`;
    });

    // Handle bold text
    const boldRegex = /\*\*(.*?)\*\*/g;
    const segments: Array<{ type: 'text' | 'bold'; content: string }> = [];
    let lastIndex = 0;
    let match;
    while ((match = boldRegex.exec(processedContent)) !== null) {
      if (match.index > lastIndex) {
        segments.push({ type: 'text', content: processedContent.slice(lastIndex, match.index) });
      }
      segments.push({ type: 'bold', content: match[1] });
      lastIndex = match.index + match[0].length;
    }
    if (lastIndex < processedContent.length) {
      segments.push({ type: 'text', content: processedContent.slice(lastIndex) });
    }

    return segments.map((segment, segIndex) => {
      const linkPlaceholderRegex = /__LINK_(\d+)__/g;
      const urlRegex = /(https?:\/\/[^\s<>"{}|\\^`]+)/g;
      const emailRegex = /([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g;
      const parts = segment.content.split(/(__LINK_\d+__)/);
      const processedParts = parts.map((part, index) => {
        const placeholderMatch = part.match(linkPlaceholderRegex);
        if (placeholderMatch) {
          const linkIndex = parseInt(placeholderMatch[0].match(/\d+/)![0]);
          const { text, url, isEmail } = linkPlaceholders[linkIndex];
          try {
            if (!isEmail) {
              new URL(url);
            }
            return (
              <a
                key={`${segIndex}-${index}`}
                href={url}
                target={isEmail ? undefined : '_blank'}
                rel={isEmail ? undefined : 'noopener noreferrer'}
                className="underline hover:no-underline text-blue-600"
              >
                {text}
              </a>
            );
          } catch {
            return <span key={`${segIndex}-${index}`}>{part}</span>;
          }
        }
        const urlParts = part.split(urlRegex);
        return urlParts.map((urlPart, urlIndex) => {
          if (urlPart.match(urlRegex)) {
            let url = urlPart.trim();
            let trailingPunctuation = '';
            const punctuationMatch = url.match(/([.,;:!?)\]]+)$/);
            if (punctuationMatch) {
              trailingPunctuation = punctuationMatch[1];
              url = url.slice(0, -trailingPunctuation.length);
            }
            try {
              new URL(url);
              return (
                <span key={`${segIndex}-${index}-${urlIndex}`}>
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline hover:no-underline text-blue-600"
                  >
                    {url}
                  </a>
                  {trailingPunctuation}
                </span>
              );
            } catch {
              return <span key={`${segIndex}-${index}-${urlIndex}`}>{urlPart}</span>;
            }
          }
          const emailParts = urlPart.split(emailRegex);
          return emailParts.map((emailPart, emailIndex) => {
            if (emailPart.match(emailRegex)) {
              return (
                <a
                  key={`${segIndex}-${index}-${urlIndex}-${emailIndex}`}
                  href={`mailto:${emailPart}`}
                  className="underline hover:no-underline text-blue-600"
                >
                  {emailPart}
                </a>
              );
            }
            return <span key={`${segIndex}-${index}-${urlIndex}-${emailIndex}`}>{emailPart}</span>;
          });
        });
      });
      if (segment.type === 'bold') {
        return <strong key={segIndex}>{processedParts}</strong>;
      }
      return <span key={segIndex}>{processedParts}</span>;
    });
  };

  return (
    <div className={cn('space-y-4', className)}>
      {messages
        .filter((message) => {
          // Filter out tool call messages without content
          if (message.role === 'assistant' && (message as unknown as { toolCalls?: unknown }).toolCalls) {
            return !!(message as unknown as { content?: unknown }).content;
          }
          return true;
        })
        .map((message, index) => {
          const isUser = message.role === 'user';
          const isAssistant = message.role === 'assistant';
          const isTool = message.role === 'tool';

          return (
            <div key={index} className={cn('flex items-end gap-2', isUser ? 'justify-end' : 'justify-start')}>
              <div
                className={cn(
                  'max-w-[75%] px-4 py-3 text-sm rounded-2xl',
                  isUser
                    ? 'text-white rounded-br-md bg-[rgba(65,39,212,0.9)]'
                    : 'bg-gray-100 text-gray-700 rounded-bl-md'
                )}
              >
                {isAssistant && (
                  <div className="flex items-center gap-2 mb-2">
                    <div className="h-5 w-5 rounded-full flex items-center justify-center bg-[#4127D4]">
                      <Bot className="h-3 w-3 text-white" />
                    </div>
                    <span className="font-medium text-sm text-gray-600">{agentName || 'Chat Assistant'}</span>
                  </div>
                )}
                <p className="whitespace-pre-wrap break-words">
                  {isTool
                    ? JSON.stringify('content' in message ? message.content : '', null, 2)
                    : renderMessageContent(removeUserOptions('content' in message ? message.content : ''))}
                </p>
              </div>
            </div>
          );
        })}
    </div>
  );
};
